import { api } from './api'

/** The allowlisted presentation shape returned by RoundMediaJSON. */
export type RoundMediaKind = 'photo' | 'video'
export type RoundMediaState = 'intended' | 'uploaded' | 'processing' | 'ready' | 'failed' | 'removed'

export type RoundMediaSubject =
  | {
      kind: 'round_player'
      round_player_id: string
      display_name: string
      guest: boolean
    }
  | {
      kind: 'unseated_uploader' | 'round_moment'
      display_name: string
    }

export interface RoundMediaItem {
  id: string
  /** Attach intents have no kind until the native picker resolves. */
  kind: RoundMediaKind | null
  state: RoundMediaState
  hole_number: number
  uploader_display_name: string
  removable: boolean
  duration_seconds: number | null
  dimensions: { width: number; height: number } | null
  audio_present: boolean | null
  subject: RoundMediaSubject
  shot: { id: string; sequence: number; club: string | null } | null
  /**
   * Same-origin authorization endpoints, never provider URLs. They are absent
   * until the corresponding reauthorizing byte route exists.
   */
  content_path?: string
  poster_path?: string | null
}

export interface RoundMediaResponse {
  media: RoundMediaItem[]
}

/** Ready-item counts for scorecard and hole-control media indicators. */
export function roundMediaHoleCounts(
  media: readonly RoundMediaItem[],
): ReadonlyMap<number, number> {
  const counts = new Map<number, number>()
  media.forEach((item) => {
    if (item.state !== 'ready' || item.kind === null) return
    counts.set(item.hole_number, (counts.get(item.hole_number) ?? 0) + 1)
  })
  return new Map([...counts.entries()].sort(([left], [right]) => left - right))
}

/** Completed-round media for a signed-in participant or club member. */
export function readRoundMedia(roundId: string): Promise<RoundMediaResponse> {
  return api.get<RoundMediaResponse>(`/golf/rounds/${encodeURIComponent(roundId)}/media`)
}

/** Ready-only media beneath the public round token; this reads no session. */
export function readSharedRoundMedia(token: string): Promise<RoundMediaResponse> {
  return api.get<RoundMediaResponse>(
    `/golf/shared_rounds/${encodeURIComponent(token)}/media`,
  )
}

/** Privacy removal for an authorized signed-in viewer. */
export function removeRoundMedia(roundId: string, mediaId: string): Promise<void> {
  return api.delete<void>(
    `/golf/rounds/${encodeURIComponent(roundId)}/media/${encodeURIComponent(mediaId)}`,
  )
}

/**
 * A browser-playable path only when the server supplied its own API route.
 * Missing delivery routes keep the item out of the player instead of leaking
 * a storage/provider URL into the DOM.
 */
export function roundMediaContentPath(item: RoundMediaItem): string | null {
  return sameOriginMediaPath(item.content_path)
}

export function roundMediaPosterPath(item: RoundMediaItem): string | null {
  return item.kind === 'video' ? sameOriginMediaPath(item.poster_path) : null
}

function sameOriginMediaPath(path: string | null | undefined): string | null {
  return path?.startsWith('/api/v1/golf/') ? path : null
}
